import React, { memo, useCallback, useMemo, useState } from "react"

import { Box, Button, Container, Flex, HStack, Stack, StackDivider, Text } from "@chakra-ui/react"

import { useConfigContext } from "@providers/config"
import { useLocationContext } from "@providers/location"
import { ClickAndCollectProvider } from "@providers/clickAndCollect"
import { useShopifyProductRaw, useShopifyProductLive, useShopifyVariants } from "@hooks/useShopify"

import useProductTemplate from "@hooks/useProductTemplate"
import { navigate } from "gatsby"

import Breadcrumb from "@app/components/Breadcrumb"
import GiftRegistryDrawer from "@app/components/GiftRegistry/Drawer"
import ProductFindInStore from "@app/components/ProductFindInStore"
import FeaturedArticles from "@app/components/Sections/FeaturedArticles/FeaturedArticles"
import Icon from "../Icon/Icon"

import ProductAddToCart from "./ProductAddToCart"
import ProductGiftRegistry from "./ProductGiftRegistry"
import RecentlyViewedProducts from "@app/components/Sections/RecentlyViewedProducts/RecentlyViewedProducts"
import ProductBadges from "./ProductBadges"
import ProductBenefits from "./ProductBenefits"
import ProductBuyNowPayLater from "./ProductBuyNowPayLater"
import ProductColors from "./ProductColors"
import ProductCompleteLook from "./ProductCompleteLook"
import ProductDetails from "./ProductDetails"
import ProductDiagram from "./ProductDiagram"
import ProductGiftCardForm from "./ProductGiftCardForm"
import ProductLinks from "./ProductLinks"
import ProductLocationCard from "./ProductLocationCard"
import ProductMedia from "./ProductMedia"
import ProductOptions from "./ProductOptions"
import ProductPrice from "./ProductPrice"
import ProductQuantity from "./ProductQuantity"
import ProductRecommended from "./ProductRecommended"
import ProductReviews from "./ProductReviews"
import ProductReviewsBadge from "./ProductReviewsBadge"
import ProductSections from "./ProductSections"
import ProductSizes from "./ProductSizes"
import ProductTitle from "./ProductTitle"
import ProductWishlist from "./ProductWishlist"
import { useCartContext } from "@providers/cart"
import { useYotpoScript } from "@app/hooks/useYotpoScript"
import { useAppContext } from "@app/providers/app"
import type { Location } from "@root/types/global"
import ProductShopTheLookProducts from "./ProductShopTheLookProducts"
import { useCore } from "@app/hooks/useCore"
import GiftRegistryPasswordDrawer from "../GiftRegistry/PasswordDrawer"

import "./ProductYotpo.module.css"
import InstagramProducts from "../Sections/InstagramProducts/InstagramProducts"
import { useMedia } from "@app/hooks/useMedia"

export interface ProductProps {
  product: GatsbyTypes.SanityProduct
  location: Location
}

const Product = ({ location, product: sanityProduct }: ProductProps) => {
  const {
    settings: { routes },
  } = useConfigContext()
  const {
    helpers: { decodeShopifyId },
  } = useCore()
  const { cart } = useCartContext()
  const { isBase } = useMedia()

  const [quantity, setQuantity] = useState<number>(1)
  const [showClickAndCollect, setShowClickAndCollect] = useState<boolean>(false)
  const [viewStore, setViewStore] = useState<NormalisedStore | null>(null)
  const [isStoreSelected, setStoreSelected] = useState<NormalisedStore | undefined>()
  const [showPasswordDrawer, setShowPasswordDrawer] = useState(false)
  const [giftRegistryId, setGiftRegistryId] = useState()

  const isStlProduct = useMemo(() => sanityProduct?.tags?.includes("stl"), [sanityProduct?.tags])

  // Set notes as custome attributes for gift cards from ProductGiftCardForm
  const [recipent, setRecipent] = useState("")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [isFormValid, setIsFormValid] = useState(false)

  const { locating } = useLocationContext()
  const { product: rawProduct } = useShopifyProductRaw(sanityProduct)
  const { product: liveProduct, loading } = useShopifyProductLive(rawProduct)

  const setDrawerOff = () => {
    setShowPasswordDrawer(false)
  }

  const product = useMemo(() => {
    return liveProduct || rawProduct
  }, [liveProduct, rawProduct])
  const { selectedOptions, handleOptions } = useShopifyVariants({ firstAvailable: true, useParameter: true, product, loading })

  const isLoading = loading || locating
  const { keyBenefits, productDetails, relatedConfig, pdpCarouselSettings } = useProductTemplate()

  const infoAndCareMetafield = useMemo(
    () =>
      product.metafields
        ?.find(m => m?.key === "info_and_care")
        ?.value?.replace(/[\n]/g, "<br/>")
        .replace(/ {2}/g, "<br>")
        .replace(/:/g, ": "),
    [product.metafields]
  )

  const selectedVariant = useMemo(() => {
    const ret = product?.variants?.find(
      variant =>
        variant?.selectedOptions?.[0]?.value === selectedOptions?.[0]?.value &&
        variant?.selectedOptions?.[1]?.value === selectedOptions?.[1]?.value
    )
    return ret
  }, [product?.variants, selectedOptions])

  const onIncrement = useCallback(
    (amount: number) => setQuantity(qty => (qty + amount <= selectedVariant.quantityAvailable ? qty + amount : qty)),
    [selectedVariant?.quantityAvailable]
  )
  const onDecrement = useCallback((amount: number) => setQuantity(qty => Math.max(1, qty - amount)), [])

  const handleChange = useCallback(
    (value: string) => {
      navigate(`${routes.PRODUCT}/${value}`)
    },
    [routes.PRODUCT]
  )

  const isGiftCard = useMemo(() => product?.productType?.toLowerCase() === "gift card", [product])

  const customAttributes = isGiftCard
    ? [
        {
          key: "To",
          value: recipent,
        },
        {
          key: "From",
          value: name,
        },
        {
          key: "Email",
          value: email,
        },
        {
          key: "Message",
          value: message,
        },
      ]
    : []

  const cartQuantity = useMemo(() => {
    const line = cart?.lines?.find(item => item.merchandise?.id === selectedVariant?.id)
    const ret = line?.quantity || 0
    return ret
  }, [selectedVariant?.id, cart])

  const productCount = useMemo(() => {
    return cartQuantity || 1
  }, [cartQuantity])

  const productSkus = useMemo(() => {
    const ret = [selectedVariant?.sku]
    return ret
  }, [selectedVariant?.sku])

  const productOptions = useMemo(() => {
    return product.options?.map(option => {
      return {
        ...option,
        changeURL: true,
      }
    })
  }, [product?.options])

  const { activeVariant } = useAppContext()
  const id = atob(product?.id)
    .split("/")
    .pop()

  useYotpoScript(sanityProduct.id, true)
  const productId = useMemo(() => decodeShopifyId(product?.id, "Product"), [decodeShopifyId, product?.id])

  return (
    <ClickAndCollectProvider productSkus={productSkus} productCount={productCount} showClickAndCollect={true}>
      <Container as="section" mt={isBase ? 2 : 3} pb={{ base: 1, md: 5 }} overflowX={{ base: "hidden", md: "initial" }}>
        <Box>
          <Container as="section" pb={5}>
            <Box ml={0}>
              <Breadcrumb data={sanityProduct} location={location} />
            </Box>
            {sanityProduct?.banner && (
              <Box backgroundColor={`#cae0e8`} textAlign={`center`} py={1} mb={2}>
                <Text size={`sm`}>{sanityProduct?.banner}</Text>
              </Box>
            )}

            <Flex flexDirection={["column", "row"]} justifyContent={`center`} columnGap={5}>
              <Box w={["100%", "65%"]} maxWidth={`813px`}>
                <ProductMedia
                  product={product}
                  images={sanityProduct?.images}
                  carouselSettings={pdpCarouselSettings}
                  maxHeight={product.tags.includes("stl") ? "823px" : "100%"}
                />
              </Box>
              {isStlProduct ? (
                <Box paddingTop={{ base: "15px", sm: "0px", md: "0px" }} w={["100%", "35%"]}>
                  <ProductTitle product={product} />
                  <ProductShopTheLookProducts product={sanityProduct} />
                </Box>
              ) : (
                <Box w={["100%", "35%"]}>
                  <Stack divider={<StackDivider m={"0 !important"} />}>
                    <Stack spacing={2} py={2.5}>
                      <ProductBadges tags={product.tags} />
                      <ProductTitle product={product} />
                      <Stack direction={{ base: `column`, md: `row` }} justifyContent={{ md: `space-between` }}>
                        <ProductPrice variant={activeVariant} direction="row" updatedQty={1} />
                        <ProductReviewsBadge product={product} />
                      </Stack>
                    </Stack>
                    {isGiftCard ? (
                      <Stack spacing={2.5} py={2.5} divider={<StackDivider />}>
                        <Flex gap={2}>
                          {productOptions.map((option, key) => (
                            <ProductOptions
                              option={option}
                              activeOptions={selectedOptions}
                              handleOptions={handleOptions}
                              loading={isLoading}
                              showTitle
                              key={key}
                            />
                          ))}
                        </Flex>
                        <ProductGiftCardForm
                          setRecipent={setRecipent}
                          setName={setName}
                          setEmail={setEmail}
                          setMessage={setMessage}
                          setIsFormValid={setIsFormValid}
                        />
                      </Stack>
                    ) : (
                      <Stack spacing={2} py={2}>
                        {sanityProduct?.siblings?.length > 0 && (
                          <ProductColors
                            siblings={sanityProduct?.siblings}
                            activeOptions={selectedOptions}
                            handleChange={handleChange}
                            showTitle
                          />
                        )}
                        <HStack w={"100%"} gap={2}>
                          <ProductSizes
                            productOptions={product.options}
                            activeOptions={selectedOptions}
                            handleOptions={handleOptions}
                            loading={isLoading}
                            showTitle
                            minimal={true}
                          />
                          <ProductQuantity
                            value={quantity || 0}
                            min={1}
                            onIncrement={onIncrement}
                            onDecrement={onDecrement}
                            onChange={e => setQuantity(parseFloat(e?.target?.value))}
                            showTitle
                            maxQty={selectedVariant?.quantityAvailable}
                          />
                        </HStack>
                        <ProductLinks product={product} activeOptions={selectedOptions} handleOptions={handleOptions} />
                      </Stack>
                    )}
                    <Stack spacing={2} py={3}>
                      <ProductLocationCard
                        cartQuantity={productCount}
                        setStoreSelected={setStoreSelected}
                        setShowClickAndCollect={setShowClickAndCollect}
                        setViewStore={setViewStore}
                      />
                      <ProductAddToCart
                        loading={isLoading}
                        quantity={quantity}
                        product={product}
                        customAttributes={customAttributes}
                        isFormValid={isGiftCard ? isFormValid : true}
                      >
                        {activeVariant?.availableForSale ? "Add to Cart" : "Unavailable"}
                      </ProductAddToCart>
                      {product?.handle.includes("gift-card") ? null : (
                        <Button
                          variant="blue"
                          size="lg"
                          onClick={() => {
                            setShowClickAndCollect(true)
                            setViewStore(null)
                          }}
                        >
                          {isStoreSelected ? "Change Store" : "Find In Store"}
                        </Button>
                      )}
                      <HStack divider={<StackDivider />} spacing={[2.5, 3.5]} alignSelf="center">
                        <ProductWishlist loading={isLoading} location={location} product={product} variant="text" />
                        <ProductGiftRegistry product={product} collection={false} />
                      </HStack>
                    </Stack>
                    <ProductBuyNowPayLater />
                    <ProductDetails
                      product={product}
                      keyBenefits={keyBenefits}
                      productDetails={productDetails}
                      infoAndCareMetafield={infoAndCareMetafield}
                    />
                  </Stack>
                  <Flex
                    style={{ cursor: `pointer` }}
                    alignItems={`center`}
                    justifyContent={`space-between`}
                    p={2}
                    borderBottom={`1px solid #E9E9E9`}
                  >
                    <Flex gap={1}>
                      <Text size={`lg`} fontWeight={600}>
                        Reviews
                      </Text>
                      <Box alignSelf="flex-end">
                        <ProductReviewsBadge product={product} />
                      </Box>
                    </Flex>
                    <Icon name={"arrowDown"} size="24px" />
                  </Flex>
                </Box>
              )}
            </Flex>
          </Container>
        </Box>
      </Container>
      {sanityProduct?.completeTheLook?.length > 0 && <ProductCompleteLook related={sanityProduct?.completeTheLook} location={location} />}
      <ProductSections product={sanityProduct} location={location} />
      {sanityProduct?.productBenefits && <ProductBenefits {...sanityProduct?.productBenefits} />}
      <ProductDiagram product={sanityProduct} />
      {!isStlProduct && <ProductReviews product={product} />}
      {!isStlProduct && (
        <div className="yotpo yotpo-pictures-widget" data-gallery-id="64ae0c0bb173b2469a4adfab" data-product-id={productId}></div>
      )}
      {sanityProduct?.related?.length > 0 && (
        <ProductRecommended products={sanityProduct?.related} currentProductData={sanityProduct} title={"Related Products"} />
      )}
      <FeaturedArticles articles={sanityProduct?.relatedLearnPages} display="carousel" {...relatedConfig.relatedArticlesSettings} />
      {isStlProduct && <RecentlyViewedProducts product={product} />}
      {showClickAndCollect && (
        <ProductFindInStore
          variants={product.variants}
          requiresProvider={true}
          onClose={() => setShowClickAndCollect(false)}
          setViewStore={setViewStore}
          viewStore={viewStore}
          selectedVariant={selectedVariant}
          drawerActive={showClickAndCollect}
        />
      )}
      <GiftRegistryDrawer quantity={quantity} setShowPasswordDrawer={setShowPasswordDrawer} setGiftRegistryId={setGiftRegistryId} />
      {showPasswordDrawer && (
        <GiftRegistryPasswordDrawer registry={{ Id: giftRegistryId, isPasswordProtected: true }} setDrawerOff={setDrawerOff} />
      )}
    </ClickAndCollectProvider>
  )
}
export default memo(Product)
